
// Functional imports (needed for initialize function)
import * as storeAuth from './views/store/auth.js';
import * as storeUsers from './views/store/users.js';
import otpPlugin from './views/plugins/otp.plugin.js';
import popupAuthPlugin, { showAuth, closeAuth, cancelAuth } from './views/plugins/popup.auth.plugin.js';

// Router
import addRoutes from '@martyrs/src/modules/core/views/router/addRoutes.js';
import { getAuthRoutes } from './views/router/auth.router.js';
import { getUsersRoutes, getProfileRoutes, getProfileSettingsRoutes } from './views/router/users.router.js';
import routerBackofficeUsers from './views/router/users.backoffice.router.js';

// Middlewares
import * as validationAuth from '@martyrs/src/modules/auth/views/middlewares/auth.validation.js';

import { i18nManager }  from '@martyrs/src/modules/core/views/classes/i18n.manager.js';

import locales from './locales/index.js';

// Component re-exports (enables tree shaking)
export { default as Auth } from './views/components/layouts/Auth.vue';
export { default as Invite } from './views/components/pages/Invite.vue';
export { default as Profile } from './views/components/pages/Profile.vue';
export { default as ProfileEdit } from './views/components/pages/ProfileEdit.vue';
export { default as ResetPassword } from './views/components/pages/ResetPassword.vue';
export { default as SignIn } from './views/components/pages/SignIn.vue';
export { default as SignUp } from './views/components/pages/SignUp.vue';
export { default as FeaturedUsers } from './views/components/sections/FeaturedUsers.vue';
export { default as SliderFeatures } from './views/components/sections/SliderFeatures.vue';

// Пример функции инициализации для модуля аутентификации
function initializeAuth(app, store, router, options = {}) {
  i18nManager.register('auth', locales);

  const authRoute = getAuthRoutes(options);
  addRoutes(router, { ...authRoute.config, parentName: authRoute.parentName });

  // Users client context:
  //   /users/:_id          — публичный read-only просмотр чужого
  //   /profile, /profile/* — свой профиль/настройки/дашборд (без ID в URL)
  if (!options.contexts || options.contexts.includes('client')) {
    const usersRoute = getUsersRoutes(options);
    addRoutes(router, { ...usersRoute.config, parentName: usersRoute.parentName });

    const profileRoute = getProfileRoutes(options);
    addRoutes(router, { ...profileRoute.config, parentName: profileRoute.parentName });
  }

  // Settings-маунт: настройки профиля вложенным роутом под роут проекта,
  // НЕЗАВИСИМО от contexts (проект может держать contexts: ['backoffice'],
  // не регистрируя публичные /users/:_id и /profile, но иметь настройки
  // внутри своего дашборда). Конфиг — плоские ключи в settings, в стиле
  // notifications (settings: { routeUsers: ... }):
  //   settings: { routeProfile: 'Dashboard', pathProfile: 'settings' }
  // module.manager прокидывает произвольный конфиг только внутри `settings`;
  // при ручном initialize() опции приходят плоско — поддерживаем оба пути.
  const settings = { ...options, ...(options.settings || {}) };
  if (settings.routeProfile) {
    const profileSettingsRoute = getProfileSettingsRoutes({
      route: settings.routeProfile,
      path: settings.pathProfile,
    });
    addRoutes(router, { ...profileSettingsRoute.config, parentName: profileSettingsRoute.parentName });
  }

  // Users backoffice context
  if (!options.contexts || options.contexts.includes('backoffice')) {
    addRoutes(router, {
      parentName: options.routeBackoffice || 'Backoffice Root',
      basePath: 'users',
      routes: routerBackofficeUsers,
      routeNamePrefix: 'Backoffice',
      meta: { context: 'backoffice' },
    });
  }

  store.addStore('auth', storeAuth);
  store.addStore('users', storeUsers);

  // OTP plugin для challenge-response
  app.use(otpPlugin);

  // Auth popup plugin
  app.use(popupAuthPlugin);

  app.provide('store', store);
}

const ModuleAuth = {
  initialize: initializeAuth,
  views: {
    store: {
      storeAuth,
      storeUsers,
    },
    router: {
      getAuthRoutes,
      getUsersRoutes,
    },
    middlewares: {
      validationAuth,
    },
  },
};

// Functional exports
export {
  storeAuth,
  storeUsers,
  getAuthRoutes,
  getUsersRoutes,
  validationAuth,
  initializeAuth as initialize,
  // Auth popup
  showAuth,
  closeAuth,
  cancelAuth,
};

export default ModuleAuth;
